// Employee-facing tax calculations. Delegates to the backend calculation
// engine (server/src/services/proposalCalculationService.ts) instead of
// re-implementing tax-bracket math client-side, so the numbers shown here
// always match the admin Proposal tool and the ad hoc employee calculator —
// see CLAUDE.md's "three calculation engines" note for why that mattered.
import { apiClient } from '@/api/apiClient';

const employeeToPayload = (employee) => ({
  annual_salary: Number(employee?.annual_salary || 0),
  filing_status: employee?.filing_status || 'single',
  state: employee?.state_of_residence || employee?.state || undefined,
  w4_version: employee?.w4_version || 'pre_2020',
  w4_allowances: Number(employee?.w4_allowances || employee?.dependents || 0),
  w4_step3_total: Number(employee?.w4_step3_total || 0),
  w4_step2_multiple_jobs: Boolean(employee?.w4_step2_multiple_jobs),
  pre_tax_monthly: Number(employee?.pre_tax_monthly || 0),
});

/**
 * Before/after tax breakdown and savings for a specific annual Section 125
 * amount. Mirrors the backend's calculateEmployeeTaxes exactly (same call).
 */
export async function calculateEmployeeTaxes(employee, section125Amount = 0, taxYear = new Date().getFullYear()) {
  const response = await apiClient.post('/proposals/calculate-taxes', {
    ...employeeToPayload(employee),
    section125_annual_amount: Number(section125Amount || 0),
    tax_year: taxYear,
  });
  return response.data;
}

/**
 * Optimal Section 125 amount for this employee, using their employer's own
 * candidate range when available. Same engine as the admin Proposal tool's
 * batch flow and ad hoc calculator (calculateOptimalSection125 in
 * proposalCalculationService.ts) via /proposals/calculate-single.
 */
export async function calculateOptimalSection125(employee, options = {}) {
  const response = await apiClient.post('/proposals/calculate-single', {
    ...employeeToPayload(employee),
    employer_id: employee?.employer_id || undefined,
    settings: {
      tax_year: options?.taxYear,
      claim_percent: options?.claimPercent,
      tax_floor: options?.taxFloor,
    },
  });

  const { eligible, computed_data } = response.data || {};
  if (!eligible || !computed_data) {
    return { optimalAmount: 0, maxAnnualBenefit: 0, maxMonthlyBenefit: 0, selectedResult: null };
  }

  return {
    optimalAmount: Number(computed_data.section125_monthly ?? computed_data.section125 ?? 0),
    maxAnnualBenefit: Number(computed_data.net_flex || 0) * 12,
    maxMonthlyBenefit: Number(computed_data.net_flex || 0),
    selectedResult: computed_data,
  };
}

/**
 * Calculate age from date of birth (pure utility, no tax data involved).
 */
export function calculateAge(dateOfBirth) {
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
}
